import React, { useEffect, useState, useRef } from 'react';
import { Form } from 'react-bootstrap';
import ModalCustom from '../../UI/modal';
import Button from '../../UI/button';
import tripsService from '../../../services/trips/trips.service';
import { useMoveDestination } from './use-move-destination';
import { useTrip } from '../../contexts/trip-context';

type Props = {
  show: boolean;
  handleClose: () => void;
  tripId?: string;
  destinationId?: string;
  destinationName?: string;
  deleteAfterMove?: boolean;
};

interface TripOption {
  id: string;
  title: string;
  destinationCount: number;
}

const MoveDestinationModal = ({
  show,
  handleClose,
  tripId: propTripId,
  destinationId: propSourceDestinationId,
  destinationName,
  deleteAfterMove
}: Props) => {
  const { trip } = useTrip();

  // Use context mode if props are not provided
  const sourceTripId = propTripId || trip?.id || '';
  const sourceDestinationId = propSourceDestinationId || '';

  const [showTripDropdown, setShowTripDropdown] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<TripOption | null>(null);
  const [trips, setTrips] = useState<TripOption[]>([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const tripDropdownRef = useRef<HTMLDivElement>(null);

  console.log('MoveDestinationModal - Props received:', {
    propTripId,
    propSourceDestinationId,
    destinationName,
    deleteAfterMove
  });
  console.log('MoveDestinationModal - Resolved values:', {
    sourceTripId,
    sourceDestinationId,
    destinationName,
    deleteAfterMove
  });

  const { moveDestination, loading } = useMoveDestination(() => {
    handleClose();
  });

  // Fetch all trips on mount
  useEffect(() => {
    const fetchTrips = async () => {
      setLoadingTrips(true);
      try {
        const data = await tripsService.getAllTrips();
        // Filter out deleted trips and current trip
        const tripOptions = data
          .filter((t) => !t.deleted && t.id !== sourceTripId)
          .map((t) => ({
            id: t.id,
            title: t.title,
            destinationCount: t.destinationCount
          }));
        setTrips(tripOptions);
      } catch (error) {
        console.error('Failed to fetch trips:', error);
      } finally {
        setLoadingTrips(false);
      }
    };
    if (show) {
      fetchTrips();
    }
  }, [show, sourceTripId]);

  // Close dropdown if clicked outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        tripDropdownRef.current &&
        !tripDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTripDropdown(false);
      }
    }
    if (showTripDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTripDropdown]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedTrip) return;

    const operation = deleteAfterMove ? 'Moving' : 'Copying';
    console.log(`${operation} destination to trip:`, {
      sourceTripId,
      sourceDestinationId,
      targetTripId: selectedTrip.id,
      deleteAfterMove
    });

    await moveDestination({
      sourceTripId,
      sourceDestinationId,
      targetTripId: selectedTrip.id,
      deleteAfterMove: deleteAfterMove || false
    });
  };

  const canSubmit = selectedTrip && !loading;

  return (
    <ModalCustom
      title={
        deleteAfterMove
          ? `Move ${destinationName || 'Destination'}`
          : `Copy ${destinationName || 'Destination'}`
      }
      show={show}
      onHide={handleClose}
    >
      <Form
        className='move-destination-modal'
        onSubmit={handleSubmit}
      >
        {/* Trip Selection */}
        <div style={{ marginBottom: '24px' }}>
          <p
            className='fs-6 font-libre'
            style={{ marginBottom: '4px' }}
          >
            {deleteAfterMove ? 'Move To Trip:' : 'Copy To Trip:'}
          </p>
          <div
            style={{ position: 'relative' }}
            ref={tripDropdownRef}
          >
            <Button
              variant='outline-secondary'
              className='w-100 text-start d-flex justify-content-between align-items-center'
              style={{
                minHeight: '38px',
                fontFamily: 'Libre Baskerville, serif',
                fontSize: '1rem',
                border: '1px solid #ced4da',
                background: '#fff',
                color: '#777777'
              }}
              onClick={() => setShowTripDropdown((v) => !v)}
              type='button'
              disabled={loadingTrips}
            >
              {loadingTrips
                ? 'Loading trips...'
                : selectedTrip?.title || 'Select a trip'}
              <span style={{ float: 'right' }}>▼</span>
            </Button>
            {showTripDropdown && !loadingTrips && (
              <div
                style={{
                  position: 'absolute',
                  zIndex: 10,
                  width: '100%',
                  background: '#fff',
                  border: '1px solid #ced4da',
                  borderTop: 'none',
                  maxHeight: 300,
                  overflowY: 'auto',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                {trips.length === 0 ? (
                  <div
                    style={{
                      padding: '12px 16px',
                      color: '#999',
                      fontStyle: 'italic'
                    }}
                  >
                    No trips available
                  </div>
                ) : (
                  trips.map((tripOption) => (
                    <div
                      key={tripOption.id}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor:
                          selectedTrip?.id === tripOption.id
                            ? '#f8f9fa'
                            : 'transparent',
                        fontFamily: 'Libre Baskerville, serif'
                      }}
                      onClick={() => {
                        setSelectedTrip(tripOption);
                        setShowTripDropdown(false);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        if (selectedTrip?.id !== tripOption.id) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div>{tripOption.title}</div>
                      <small style={{ color: '#999' }}>
                        {tripOption.destinationCount} destination
                        {tripOption.destinationCount !== 1 ? 's' : ''}
                      </small>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        {/* Submit Button */}
        <Button
          disabled={!canSubmit}
          variant='primary'
          className='mx-auto'
          width={142}
          type='submit'
        >
          {loading
            ? deleteAfterMove
              ? 'Moving...'
              : 'Copying...'
            : deleteAfterMove
            ? `Move to ${selectedTrip?.title || 'Trip'}`
            : `Copy to ${selectedTrip?.title || 'Trip'}`}
        </Button>
      </Form>
    </ModalCustom>
  );
};

export default MoveDestinationModal;
